import { FRAME_RATE } from "../core/constants.js";
import { BRUTEFORCE_ACTIONS, canonicalizeBruteforceEvents, normalizeScriptEvents, parseOptionalMaxFrameToken, parseScriptFrameToken } from "./script-format.js";

function randomInt(rng, min, max) {
  if (max <= min) {
    return min;
  }
  return min + Math.floor(rng() * (max - min + 1));
}

function pickRandomIndex(rng, items) {
  if (!items.length) {
    return -1;
  }
  return items[randomInt(rng, 0, items.length - 1)];
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function parseWindow(actionConfig, fallbackWindow) {
  const minFrame = parseScriptFrameToken(actionConfig?.minTime ?? "0:0");
  const maxFrame = parseOptionalMaxFrameToken(actionConfig?.maxTime ?? "0");
  const highestFrame = fallbackWindow.maxFrame ?? (minFrame + FRAME_RATE * 2);
  const resolvedMax = maxFrame == null ? highestFrame : Math.max(minFrame, maxFrame);
  return { minFrame, maxFrame: resolvedMax };
}

function overallWindow(config, events) {
  const highestFrame = events.reduce((best, event) => Math.max(best, event.frame), FRAME_RATE * 2);
  const fallbackMax = Math.max(highestFrame + FRAME_RATE * 2, config.maxFrameHint ?? 0);
  return { minFrame: 0, maxFrame: fallbackMax };
}

function randomFrameInWindow(rng, window) {
  return randomInt(rng, window.minFrame, window.maxFrame);
}

function mutationDelta(rng, config) {
  const minDelta = Math.floor(Number(config?.deltaMinFrames ?? -FRAME_RATE) || 0);
  const maxDelta = Math.floor(Number(config?.deltaMaxFrames ?? FRAME_RATE) || 0);
  let delta = randomInt(rng, Math.min(minDelta, maxDelta), Math.max(minDelta, maxDelta));
  if (delta === 0) {
    if (minDelta < 0) {
      delta = -1;
    } else if (maxDelta > 0) {
      delta = 1;
    }
  }
  return delta;
}

function buildActionPool(events) {
  const pool = new Set(BRUTEFORCE_ACTIONS);
  for (const event of events) {
    if (BRUTEFORCE_ACTIONS.includes(event.action)) {
      pool.add(event.action);
    }
  }
  return [...pool];
}

function actionStateThroughFrame(events, action, frame) {
  let isPressed = false;
  for (const event of events) {
    if (event.action !== action) {
      continue;
    }
    if (event.frame > frame) {
      break;
    }
    isPressed = event.type === "press";
  }
  return isPressed;
}

function finalizeEvents(events) {
  return canonicalizeBruteforceEvents(events);
}

export function mutateScriptEvents(baseEvents, config, rng = Math.random) {
  const events = normalizeScriptEvents(baseEvents);
  const nextEvents = events.map((event) => ({ ...event }));
  const globalWindow = overallWindow(config, nextEvents);
  const actionPool = buildActionPool(nextEvents);

  let changed = false;

  for (const action of actionPool) {
    const actionConfig = config?.modify?.[action];
    if (!actionConfig) {
      continue;
    }
    const window = parseWindow(actionConfig, globalWindow);
    const affectedIndices = nextEvents
      .map((event, index) => (event.action === action && event.frame >= window.minFrame && event.frame <= window.maxFrame ? index : -1))
      .filter((index) => index >= 0);
    const minModify = Math.min(Math.max(0, actionConfig.minCount ?? 0), affectedIndices.length);
    const maxModify = Math.min(Math.max(minModify, actionConfig.maxCount ?? 0), affectedIndices.length);
    const modifyCount = maxModify > 0 ? randomInt(rng, minModify, maxModify) : 0;
    for (let index = 0; index < modifyCount; index += 1) {
      const targetIndex = pickRandomIndex(rng, affectedIndices);
      if (targetIndex < 0 || !nextEvents[targetIndex]) {
        continue;
      }
      nextEvents[targetIndex].frame = clamp(nextEvents[targetIndex].frame + mutationDelta(rng, actionConfig), window.minFrame, window.maxFrame);
      changed = true;
    }
  }

  const deleteIndices = new Set();
  for (const action of actionPool) {
    const actionConfig = config?.delete?.[action];
    if (!actionConfig) {
      continue;
    }
    const window = parseWindow(actionConfig, globalWindow);
    const affectedIndices = nextEvents
      .map((event, index) => (
        event.action === action
        && event.frame >= window.minFrame
        && event.frame <= window.maxFrame
        && !deleteIndices.has(index)
          ? index
          : -1
      ))
      .filter((index) => index >= 0);
    const minDelete = Math.min(Math.max(0, actionConfig.minCount ?? 0), affectedIndices.length);
    const maxDelete = Math.min(Math.max(minDelete, actionConfig.maxCount ?? 0), affectedIndices.length);
    const deleteCount = maxDelete > 0 ? randomInt(rng, minDelete, maxDelete) : 0;
    while ([...deleteIndices].filter((index) => nextEvents[index]?.action === action).length < deleteCount) {
      const targetIndex = pickRandomIndex(rng, affectedIndices.filter((index) => !deleteIndices.has(index)));
      if (targetIndex < 0) {
        break;
      }
      deleteIndices.add(targetIndex);
      changed = true;
    }
  }

  const keptEvents = nextEvents.filter((_, index) => !deleteIndices.has(index));
  let nextOrder = keptEvents.reduce((best, event) => Math.max(best, event.order ?? 0), -1) + 1;

  for (const action of actionPool) {
    const actionConfig = config?.add?.[action];
    if (!actionConfig) {
      continue;
    }
    const window = parseWindow(actionConfig, globalWindow);
    const minAdd = Math.max(0, actionConfig.minCount ?? 0);
    const maxAdd = Math.max(minAdd, actionConfig.maxCount ?? 0);
    const addCount = maxAdd > 0 ? randomInt(rng, minAdd, maxAdd) : 0;
    for (let index = 0; index < addCount; index += 1) {
      const frame = randomFrameInWindow(rng, window);
      const type = actionStateThroughFrame(keptEvents, action, frame) ? "rel" : "press";
      keptEvents.push({
        frame,
        type,
        action,
        order: nextOrder++,
      });
      changed = true;
    }
  }

  if (!changed) {
    for (const action of actionPool) {
      const actionConfig = config?.modify?.[action];
      if (!actionConfig || Number(actionConfig.maxCount ?? 0) <= 0) {
        continue;
      }
      const window = parseWindow(actionConfig, globalWindow);
      const indices = keptEvents
        .map((event, index) => (event.action === action && event.frame >= window.minFrame && event.frame <= window.maxFrame ? index : -1))
        .filter((index) => index >= 0);
      if (indices.length > 0) {
        const targetIndex = indices[randomInt(rng, 0, indices.length - 1)];
        keptEvents[targetIndex].frame = clamp(keptEvents[targetIndex].frame + mutationDelta(rng, actionConfig), window.minFrame, window.maxFrame);
        changed = true;
        break;
      }
    }
  }

  return finalizeEvents(keptEvents);
}
